import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Badge, Select, Card, Skeleton, Tooltip, Row, Col } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  EyeOutlined,
  FilterOutlined,
  PhoneOutlined,
  TeamOutlined,
  CarOutlined,
  WifiOutlined,
  StopOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { CallNotesModal } from '../../components/CallNotesModal';
import { CallColorFilters, type CallColor } from '../../components/CallColorFilters';
import { LanguageFlag, RolePair } from '../../components/UserBadges';
import { useEffect, useRef, useState } from 'react';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';
import { t } from '../../i18n';
import { formatRegisteredAt, resolveCallIconColor } from '../../lib/utils';
import { apiClient } from '../../lib/api-client';
import { buildUserListFilters, countDrawerFilters } from '../../lib/userListFilters';

interface User {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  initialRole: 'CLIENT' | 'DRIVER' | 'ADMIN' | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  isDriverVerified: boolean;
  createdAt: string;
  preferredLang: 'UZ' | 'RU' | 'EN' | null;
  callNotesCount?: number;
  callIconColor?: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | null;
}

export const UserList = () => {
  const { show } = useNavigation();
  const [filterOpen, setFilterOpen] = useState(false);

  // Unified filter state. Every UI change funnels through commit() so the
  // refine filter list is always rebuilt from this single source of truth.
  const [searchText, setSearchText] = useState('');
  const [drawerFilters, setDrawerFilters] = useState<UserFilterValues>({});
  const [callColorFilter, setCallColorFilter] = useState<CallColor | null>(null);
  const [roleSelect, setRoleSelect] = useState<string | undefined>(undefined);
  const [statusSelect, setStatusSelect] = useState<string | undefined>(undefined);
  const searchTimerRef = useRef<number | null>(null);

  const activeFilterCount = countDrawerFilters(drawerFilters);

  const { tableProps, setFilters, tableQueryResult } = useTable<User>({
    resource: 'admin/users',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    filters: {
      initial: [],
    },
  });

  /**
   * Apply the consolidated filter state to refine. We use 'replace' mode so
   * clearing a Select / removing a drawer field actually removes it — refine
   * v4's default 'merge' would silently keep the old field.
   *
   * Presence of a key in `next` (via `'k' in next`) determines what's being
   * changed — checking the value would treat "set to undefined" (= the
   * Select's clear-X output) as "no change" and silently keep the old
   * value.
   */
  const commit = (next: {
    search?: string;
    drawer?: UserFilterValues;
    callColor?: CallColor | null;
    role?: string | undefined;
    status?: string | undefined;
  }) => {
    const search = 'search' in next ? next.search! : searchText;
    const drawer = 'drawer' in next ? next.drawer! : drawerFilters;
    const callColor = 'callColor' in next ? next.callColor! : callColorFilter;
    const role = 'role' in next ? next.role : roleSelect;
    const status = 'status' in next ? next.status : statusSelect;
    if ('search' in next) setSearchText(search);
    if ('drawer' in next) setDrawerFilters(drawer);
    if ('callColor' in next) setCallColorFilter(callColor);
    if ('role' in next) setRoleSelect(role);
    if ('status' in next) setStatusSelect(status);
    setFilters(
      buildUserListFilters({ search, drawer, callColor, role, status }),
      'replace',
    );
  };

  // Stat cards above the table — same visual language as DriverList.
  const [stats, setStats] = useState<{
    total: number;
    drivers: number;
    passengers: number;
    online: number;
    onlineDrivers: number;
    onlinePassengers: number;
    blocked: number;
    newThisMonth: number;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/admin/users-stats');
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Operator-call-notes modal state
  const [callModalUser, setCallModalUser] = useState<User | null>(null);

  const handleSearchChange = (val: string) => {
    setSearchText(val); // immediate UI feedback
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    // Apply immediately on clear, debounce 350ms while typing.
    searchTimerRef.current = window.setTimeout(
      () => commit({ search: val }),
      val === '' ? 0 : 350,
    );
  };

  // Drawer "Apply" — replaces the drawer field set with whatever the form
  // returned. Search / role / status / callColour stay as-is unless the
  // drawer explicitly returned values for them (it normally doesn't).
  const handleDrawerApply = (drawer: UserFilterValues) => {
    const { search, role, status, ...rest } = drawer;
    const update: any = { drawer: rest };
    if (search !== undefined) update.search = search;
    if (role !== undefined) update.role = role;
    if (status !== undefined) update.status = status;
    commit(update);
  };

  const handleResetFilters = () => {
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    commit({
      search: '',
      drawer: {},
      callColor: null,
      role: undefined,
      status: undefined,
    });
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, []);

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
    RESTING: 'orange',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: t('status.online'),
    OFFLINE: t('status.offline'),
    BLOCKED: t('status.blocked'),
    RESTING: t('status.resting'),
  };

  // Role labels are now rendered by <RolePair /> from components/UserBadges.

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => <span style={{ fontWeight: 500 }}>#{id}</span>,
    },
    {
      title: t('table.user'),
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: User) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || t('common.noName')}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('table.role'),
      key: 'role',
      render: (_: unknown, record: User) => (
        <RolePair currentRole={record.currentRole} initialRole={record.initialRole} />
      ),
    },
    {
      title: t('users.language'),
      key: 'preferredLang',
      width: 60,
      render: (_: unknown, record: User) => <LanguageFlag lang={record.preferredLang} />,
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
      ),
    },
    {
      title: t('table.rating'),
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number | string) => rating != null ? Number(rating).toFixed(1) : '-',
    },
    {
      title: t('table.trips'),
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: t('table.registered'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend' as const,
      render: (date: string) => formatRegisteredAt(date),
    },
    {
      title: '',
      key: 'call',
      width: 60,
      render: (_: unknown, record: User) => {
        const color = resolveCallIconColor(record.callIconColor, record.callNotesCount);
        const called = (record.callNotesCount ?? 0) > 0;
        return (
          <Tooltip
            title={
              record.callIconColor
                ? `Цветная метка: ${record.callIconColor}`
                : called
                  ? `Был(а) звонок: ${record.callNotesCount}`
                  : 'Звонков ещё не было — добавить заметку'
            }
          >
            <Button
              type="text"
              icon={<PhoneOutlined style={{ color }} />}
              onClick={(e) => {
                e.stopPropagation();
                setCallModalUser(record);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('table.actions'),
      key: 'actions',
      render: (_: unknown, record: User) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => show('admin/users', record.id)}
        />
      ),
    },
  ];

  const statCards = stats
    ? [
        {
          key: 'total',
          title: t('usersStats.total'),
          value: stats.total,
          icon: <TeamOutlined />,
          color: '#1F2937',
          bg: '#F3F4F6',
        },
        {
          key: 'drivers',
          title: t('usersStats.drivers'),
          value: stats.drivers,
          icon: <CarOutlined />,
          color: '#3B82F6',
          bg: '#EFF6FF',
        },
        {
          key: 'passengers',
          title: t('usersStats.passengers'),
          value: stats.passengers,
          icon: <UserOutlined />,
          color: '#10B981',
          bg: '#ECFDF5',
        },
        {
          key: 'online',
          title: t('usersStats.online'),
          value: stats.online,
          suffix:
            stats.onlineDrivers + stats.onlinePassengers > 0
              ? `${stats.onlineDrivers} + ${stats.onlinePassengers}`
              : undefined,
          icon: <WifiOutlined />,
          color: '#0EA5E9',
          bg: '#F0F9FF',
        },
        {
          key: 'blocked',
          title: t('usersStats.blocked'),
          value: stats.blocked,
          icon: <StopOutlined />,
          color: '#EF4444',
          bg: '#FEF2F2',
        },
        {
          key: 'newThisMonth',
          title: t('usersStats.newThisMonth'),
          value: stats.newThisMonth,
          icon: <RiseOutlined />,
          color: '#8B5CF6',
          bg: '#F5F3FF',
        },
      ]
    : [];

  return (
    <>
      {/* Stat cards above the table — match DriverList visual language. */}
      {statsLoading ? (
        <Card style={{ marginBottom: 16 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ) : stats ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {statCards.map((c) => (
            <Col key={c.key} xs={12} sm={8} md={6} lg={4}>
              <Card
                size="small"
                bordered={false}
                style={{ background: c.bg, borderRadius: 10, height: '100%' }}
                bodyStyle={{ padding: 12 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: c.color,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {c.icon}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#6B7280',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.title}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {c.value}
                      {c.suffix && (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#9CA3AF',
                            marginLeft: 4,
                          }}
                        >
                          ({c.suffix})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      <List
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{t('users.allUsers')}</span>
            <CallColorFilters
              value={callColorFilter}
              onChange={(color) => commit({ callColor: color })}
            />
          </span>
        }
        headerButtons={
          <Space>
            <Input
              placeholder={t('common.search')}
              prefix={<SearchOutlined />}
              allowClear
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder={t('table.role')}
              allowClear
              style={{ width: 130 }}
              value={roleSelect}
              onChange={(value) => commit({ role: value })}
              options={[
                { value: 'CLIENT', label: t('role.passenger') },
                { value: 'DRIVER', label: t('role.driver') },
              ]}
            />
            <Select
              placeholder={t('table.status')}
              allowClear
              style={{ width: 130 }}
              value={statusSelect}
              onChange={(value) => commit({ status: value })}
              options={[
                { value: 'ONLINE', label: t('status.online') },
                { value: 'OFFLINE', label: t('status.offline') },
                { value: 'BLOCKED', label: t('status.blocked') },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                {t('common.filter')}
              </Button>
            </Badge>
          </Space>
        }
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          loading={
            tableProps.loading
              ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
              : false
          }
        />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleDrawerApply}
        onReset={handleResetFilters}
        filterType="all"
        initialValues={drawerFilters}
      />

      <CallNotesModal
        open={!!callModalUser}
        userId={callModalUser?.id ?? null}
        userName={callModalUser?.fullName ?? undefined}
        userPhone={callModalUser?.phone}
        currentColor={callModalUser?.callIconColor ?? null}
        onClose={() => setCallModalUser(null)}
        onChanged={() => tableQueryResult?.refetch?.()}
      />
    </>
  );
};
